import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaTimes } from 'react-icons/fa';
import './css_pages/Locations.css';
import {
  getAllLocations,
  createLocation,
  updateLocation,
  deleteLocation
} from '../services/locationsService';
import { getAllStores } from '../services/storesService';

function Locations() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [advancedSearchStore, setAdvancedSearchStore] = useState('');
  const [locations, setLocations] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storeSearch, setStoreSearch] = useState('');

  // Cargar datos al montar el componente
  useEffect(() => {
    fetchLocations();
    fetchStores();
  }, []);

  // Obtener todas las ubicaciones
  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllLocations();
      if (response.success) {
        setLocations(response.data);
      }
    } catch (error) {
      setError('Error loading locations');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener todas las tiendas
  const fetchStores = async () => {
    try {
      const response = await getAllStores();
      if (response.success) {
        setStores(response.data);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  };

  // Filtrar ubicaciones según búsqueda
  const filteredLocations = locations.filter(location => {
    const matchesName = location.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStore = advancedSearchStore === '' || 
      location.storeName.toLowerCase().includes(advancedSearchStore.toLowerCase());
    return matchesName && matchesStore;
  });

  // Agrupar ubicaciones por tienda
  const groupedLocations = filteredLocations.reduce((acc, location) => {
    const key = location.storeId;
    if (!acc[key]) {
      acc[key] = {
        storeId: location.storeId,
        storeName: location.storeName,
        locations: []
      };
    }
    acc[key].locations.push(location);
    return acc;
  }, {});

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Abrir modal para crear
  const handleOpenModal = () => {
    setShowModal(true);
    setLocationName('');
    setSelectedStore(null);
    setEditingId(null);
  };

  // Abrir modal de selección de Store
  const handleOpenStoreModal = () => {
    setShowStoreModal(true);
    setStoreSearch('');
  };

  // Seleccionar Store
  const handleSelectStore = (store) => {
    setSelectedStore(store);
    setShowStoreModal(false);
  };

  // Abrir modal para editar
  const handleEditModal = (location) => {
    setShowModal(true);
    setLocationName(location.name);
    setSelectedStore({
      id: location.storeId,
      name: location.storeName
    });
    setEditingId(location.id);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setLocationName('');
    setSelectedStore(null);
    setEditingId(null);
  };

  // Guardar o actualizar ubicación
  const handleSaveLocation = async () => {
    const normalizedName = locationName.trim();
    
    if (!selectedStore) {
      alert('Please select a store');
      return;
    }
    
    if (normalizedName === '') {
      alert('Please enter the location name');
      return;
    }

    try {
      setLoading(true);
      
      if (editingId) {
        const response = await updateLocation(editingId, {storeId: selectedStore.id, name: normalizedName});
        if (response.success) {
          alert('Location updated successfully');
          await fetchLocations();
          handleCloseModal();
        }
      } else {
        const response = await createLocation({storeId: selectedStore.id, name: normalizedName});
        if (response.success) {
          alert('Location created successfully');
          await fetchLocations();
          handleCloseModal();
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert('There is already a location with that name for this store.');
      } else {
        alert('Error saving location');
      }
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Abrir modal de confirmación de eliminación
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Confirmar eliminación
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteLocation(deleteId);
      if (response.success) {
        alert('Location successfully removed');
        await fetchLocations();
      }
      handleCloseDeleteModal();
    } catch (error) {
      alert('Error deleting location');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar stores en el modal
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(storeSearch.toLowerCase())
  );

  return (
    <div className="locations-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Locations</h1>
        </div>
        <button className="btn-primary" onClick={handleOpenModal} disabled={loading}>
          <FaPlus /> Create new location
        </button>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchLocations}>Retry</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Desktop */}
      <div className="table-container desktop-only">
        {loading && <div className="loading">Loading...</div>}
        
        <table className="data-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Location Name</th>
              <th>Creation Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(groupedLocations).length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? 'Loading...' : 'No results found'}
                </td>
              </tr>
            ) : (
              Object.values(groupedLocations).map((group) => (
                <React.Fragment key={group.storeId}>
                  {group.locations.map((location, index) => (
                    <tr key={location.id}>
                      {index === 0 && (
                        <td rowSpan={group.locations.length} className="store-cell">
                          {group.storeName}
                        </td>
                      )}
                      <td>{location.name}</td>
                      <td>{formatDate(location.createdAt)}</td>
                      <td>
                        <button 
                          className="btn-action" 
                          onClick={() => handleEditModal(location)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn-action btn-delete" 
                          onClick={() => handleOpenDeleteModal(location.id)}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards - Mobile */}
      <div className="locations-cards-container mobile-only">
        {loading && <div className="loading">Loading...</div>}
        
        {Object.keys(groupedLocations).length === 0 ? (
          <div className="no-results">
            {loading ? 'Loading...' : 'No results found'}
          </div>
        ) : (
          Object.values(groupedLocations).map((group) => (
            <div key={group.storeId} className="store-group">
              <div className="store-group-header">
                <h3>{group.storeName}</h3>
                <span className="location-count">{group.locations.length} location{group.locations.length !== 1 ? 's' : ''}</span>
              </div>
              
              {group.locations.map((location) => (
                <div key={location.id} className="location-card">
                  <div className="location-card-header">
                    <h4 className="location-card-name">{location.name}</h4>
                  </div>

                  <div className="location-card-body">
                    <div className="location-card-item">
                      <span className="location-card-label">Creation Date</span>
                      <span className="location-card-value">{formatDate(location.createdAt)}</span>
                    </div>
                  </div>

                  <div className="location-card-footer">
                    <button 
                      className="btn-card-action btn-edit" 
                      onClick={() => handleEditModal(location)}
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-card-action btn-delete-card" 
                      onClick={() => handleOpenDeleteModal(location.id)}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Modal Crear/Editar */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit location' : 'Create new location'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Store *</label>
                <div className="pick-container">
                  <button 
                    className="btn-pick" 
                    onClick={handleOpenStoreModal}
                    disabled={loading}
                  >
                    <FaSearch /> Pick
                  </button>
                  {selectedStore && (
                    <div className="selected-item">
                      {selectedStore.name}
                      <button 
                        className="btn-remove-selection"
                        onClick={() => setSelectedStore(null)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Enter the location name"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveLocation} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Seleccionar Store */}
      {showStoreModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-picker" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select a store</h2>
              <button className="btn-close" onClick={() => setShowStoreModal(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Search..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="picker-list">
                <table className="picker-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStores.map((store) => (
                      <tr key={store.id}>
                        <td>
                          <button 
                            className="btn-select-item"
                            onClick={() => handleSelectStore(store)}
                          >
                            ✓
                          </button>
                        </td>
                        <td>{store.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm deletion</h2>
              <button className="btn-close" onClick={handleCloseDeleteModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete this location?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete} disabled={loading}>
                {loading ? 'Removing...' : 'Eliminate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Locations;